import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Capacitor } from '@capacitor/core'
import { hasSeenOnboarding, shouldShowNativeOnboarding } from '@/utils/onboarding'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public marketing + onboarding
    {
      path: '/',
      component: () => import('@/layouts/PublicLayout.vue'),
      meta: { requiresAuth: false },
      children: [
        {
          path: '',
          name: 'Home',
          component: () => import('@/views/public/HomeView.vue')
        },
        {
          path: 'about',
          name: 'About',
          component: () => import('@/views/public/AboutView.vue')
        },
        {
          path: 'classes',
          name: 'Classes',
          component: () => import('@/views/public/ClassesView.vue')
        },
        {
          path: 'results',
          name: 'Results',
          component: () => import('@/views/public/ResultsView.vue')
        },
        {
          path: 'app',
          name: 'App',
          component: () => import('@/views/public/AppView.vue')
        },
        {
          path: 'for-parents',
          redirect: '/app#parents'
        },
        {
          path: 'for-teachers',
          redirect: '/app#teachers'
        },
        {
          path: 'for-admin',
          redirect: '/app#institute'
        }
      ]
    },
    {
      path: '/welcome',
      name: 'Welcome',
      component: () => import('@/views/public/WelcomeView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/who-are-you',
      name: 'WhoAreYou',
      component: () => import('@/views/public/WhoAreYouView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/explore-mastermind',
      name: 'ExploreMasterMind',
      component: () => import('@/views/public/ExploreMasterMindView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: () => import('@/views/PrivacyPolicyView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/data-deletion',
      name: 'DataDeletion',
      component: () => import('@/views/DataDeletionView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/accept-invitation',
      name: 'AcceptInvitation',
      component: () => import('@/views/auth/AcceptInvitationView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/otp-verify',
      name: 'OtpVerify',
      component: () => import('@/views/auth/OtpVerifyView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/change-password',
      name: 'ChangePassword',
      component: () => import('@/views/admin/ChangePasswordView.vue'),
      meta: { requiresAuth: true, roles: ['Admin', 'Teacher', 'Parent'] }
    },

    // Admin routes
    {
      path: '/admin',
      component: () => import('@/layouts/AdminLayout.vue'),
      meta: { requiresAuth: true, roles: ['Admin'] },
      children: [
        {
          path: '',
          name: 'AdminDashboard',
          component: () => import('@/views/admin/DashboardView.vue')
        },
        {
          path: 'sessions',
          name: 'AdminSessions',
          component: () => import('@/views/admin/SessionsView.vue')
        },
        {
          path: 'sessions/:id',
          name: 'AdminSessionDetail',
          component: () => import('@/views/admin/SessionDetailView.vue')
        },
        {
          path: 'students',
          name: 'AdminStudents',
          component: () => import('@/views/admin/StudentsView.vue')
        },
        {
          path: 'classes',
          name: 'AdminClasses',
          component: () => import('@/views/admin/ClassesView.vue')
        },
        {
          path: 'attendance',
          name: 'AdminAttendance',
          component: () => import('@/views/admin/AttendanceView.vue')
        },
        {
          path: 'daily-expenses',
          name: 'AdminDailyExpenses',
          component: () => import('@/views/admin/finance/DailyExpensesView.vue')
        },
        {
          path: 'finance',
          component: () => import('@/layouts/FinanceLayout.vue'),
          children: [
            {
              path: '',
              name: 'FinanceOverview',
              component: () => import('@/views/admin/finance/FinanceOverview.vue')
            },
            {
              path: 'fees',
              name: 'FeesManagement',
              component: () => import('@/views/admin/finance/FeesManagementView.vue')
            },
            {
              path: 'ledger',
              name: 'FinanceHouseholdLedger',
              component: () => import('@/views/admin/finance/HouseholdLedgerView.vue')
            },
            {
              path: 'archive',
              name: 'FinanceArchive',
              component: () => import('@/views/admin/finance/FinanceArchiveView.vue')
            },
            {
              path: 'fee-collection',
              redirect: '/admin/finance/ledger'
            },
            {
              path: 'transactions',
              redirect: '/admin/finance/ledger'
            },
            {
              path: 'expenses',
              name: 'ExpensesManagement',
              component: () => import('@/views/admin/finance/ExpensesView.vue')
            },
            {
              path: 'overdue',
              name: 'OverdueFees',
              component: () => import('@/views/admin/finance/OverdueFeesView.vue')
            },
            {
              path: 'collections',
              name: 'FinanceCollections',
              component: () => import('@/views/admin/finance/CollectionsView.vue')
            },
            {
              path: 'reminders',
              name: 'FinanceReminders',
              component: () => import('@/views/admin/finance/RemindersView.vue')
            },
            {
              path: 'reports',
              name: 'FinanceReports',
              component: () => import('@/views/admin/finance/FinanceReportsView.vue')
            },
            {
              path: 'templates',
              redirect: '/admin/template-zone'
            }
          ]
        },
        {
          path: 'notices',
          name: 'AdminNotices',
          component: () => import('@/views/admin/NoticesView.vue')
        },
        {
          path: 'template-zone',
          name: 'AdminTemplateZone',
          component: () => import('@/views/admin/finance/TemplateZoneView.vue')
        },
        {
          path: 'notes-tracker',
          name: 'AdminNotesTracker',
          component: () => import('@/views/admin/NotesTrackerView.vue')
        },
        {
          path: 'academic-planner',
          name: 'AdminAcademicPlanner',
          component: () => import('@/views/admin/AcademicPlannerView.vue')
        },
        {
          path: 'paper-generator',
          name: 'AdminPaperGenerator',
          component: () => import('@/views/admin/PaperGeneratorView.vue')
        },
        {
          path: 'teachers',
          name: 'AdminTeachers',
          component: () => import('@/views/admin/TeachersView.vue')
        },
        {
          path: 'leads',
          name: 'AdminLeads',
          component: () => import('@/views/admin/LeadsView.vue')
        },
        {
          path: 'achievements',
          name: 'AdminAchievements',
          component: () => import('@/views/admin/AchievementsView.vue')
        },
        {
          path: 'change-password',
          name: 'AdminChangePassword',
          component: () => import('@/views/admin/ChangePasswordView.vue')
        }
      ]
    },

    // Teacher routes
    {
      path: '/teacher',
      component: () => import('@/layouts/TeacherLayout.vue'),
      meta: { requiresAuth: true, roles: ['Teacher'] },
      children: [
        {
          path: '',
          name: 'TeacherDashboard',
          component: () => import('@/views/teacher/DashboardView.vue')
        },
        {
          path: 'students',
          name: 'TeacherStudents',
          component: () => import('@/views/teacher/StudentsView.vue')
        },
        {
          path: 'attendance',
          name: 'TeacherAttendance',
          component: () => import('@/views/teacher/AttendanceView.vue')
        },
        {
          path: 'remarks',
          name: 'TeacherRemarks',
          component: () => import('@/views/teacher/RemarksView.vue')
        },
        {
          path: 'homework',
          name: 'TeacherHomework',
          component: () => import('@/views/teacher/HomeworkView.vue')
        },
        {
          path: 'class-performance',
          name: 'TeacherClassPerformance',
          component: () => import('@/views/teacher/ClassPerformanceView.vue')
        },
        {
          path: 'academic-planner',
          name: 'TeacherAcademicPlanner',
          component: () => import('@/views/admin/AcademicPlannerView.vue')
        }
      ]
    },

    // Parent routes
    {
      path: '/parent',
      component: () => import('@/layouts/ParentLayout.vue'),
      meta: { requiresAuth: true, roles: ['Parent'] },
      children: [
        {
          path: '',
          name: 'ParentDashboard',
          component: () => import('@/views/parent/DashboardView.vue')
        },
        {
          path: 'attendance',
          name: 'ParentAttendance',
          component: () => import('@/views/parent/AttendanceView.vue')
        },
        {
          path: 'fees',
          name: 'ParentFees',
          component: () => import('@/views/parent/FeesView.vue')
        },
        {
          path: 'performance',
          name: 'ParentPerformance',
          component: () => import('@/views/parent/PerformanceView.vue')
        },
        {
          path: 'homework',
          name: 'ParentHomework',
          component: () => import('@/views/parent/HomeworkView.vue')
        },
        {
          path: 'achievements',
          name: 'ParentAchievements',
          component: () => import('@/views/parent/AchievementsView.vue')
        },
        {
          path: 'notices',
          name: 'ParentNotices',
          component: () => import('@/views/parent/NoticesView.vue')
        },
        {
          path: 'notification-history',
          name: 'ParentNotificationHistory',
          redirect: { name: 'ParentNotices', hash: '#notification-history' }
        },
        {
          path: 'account-security',
          name: 'ParentAccountSecurity',
          component: () => import('@/views/parent/AccountSecurityView.vue')
        }
      ]
    },

    // Catch all route
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFoundView.vue')
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // First native launch only: marketing Home → Welcome. Never trap /login.
  if (shouldShowNativeOnboarding() && !authStore.isAuthenticated && to.name === 'Home') {
    next({ name: 'Welcome' })
    return
  }

  // Later native launches: Home → Login (session restore still wins when authenticated).
  if (
    Capacitor.isNativePlatform() &&
    hasSeenOnboarding() &&
    !authStore.isAuthenticated &&
    to.name === 'Home'
  ) {
    next({ name: 'Login' })
    return
  }

  // Logged-in users should not re-enter marketing/onboarding on app open.
  if (
    authStore.isAuthenticated &&
    (to.name === 'Home' || to.name === 'Welcome' || to.name === 'WhoAreYou')
  ) {
    const role = authStore.user?.role
    if (role === 'Admin') {
      next({ name: 'AdminDashboard' })
      return
    }
    if (role === 'Teacher') {
      next({ name: 'TeacherDashboard' })
      return
    }
    if (role === 'Parent') {
      next({ name: 'ParentDashboard' })
      return
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    // Check if user is authenticated
    if (!authStore.isAuthenticated) {
      next({ name: 'Login' })
      return
    }

    // Check if user has required role
    if (to.meta.roles && !(to.meta.roles as string[]).includes(authStore.user?.role || '')) {
      // Redirect to appropriate dashboard based on role
      const role = authStore.user?.role
      if (role === 'Admin') {
        next({ name: 'AdminDashboard' })
      } else if (role === 'Teacher') {
        next({ name: 'TeacherDashboard' })
      } else if (role === 'Parent') {
        next({ name: 'ParentDashboard' })
      } else {
        next({ name: 'Login' })
      }
      return
    }
  }

  // If user is authenticated and trying to access login page, redirect to dashboard
  if (authStore.isAuthenticated && to.name === 'Login') {
    const role = authStore.user?.role
    if (role === 'Admin') {
      next({ name: 'AdminDashboard' })
    } else if (role === 'Teacher') {
      next({ name: 'TeacherDashboard' })
    } else if (role === 'Parent') {
      next({ name: 'ParentDashboard' })
    } else {
      next()
    }
    return
  }

  next()
})

export default router
